import React, { useEffect, useRef, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  InputAdornment, Paper, Radio, TextField, useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import { searchPlaces } from '../../utils/mapbox'
import { C, FONT, primaryButtonSx, tnum } from './tokens'
import { CREATE_MANUAL_DELIVERY, MANUAL_DELIVERY_QUOTE } from './newDeliveryQueries'
import { buildOrderInput, lineDetails } from './menuLine'
import MenuItemPicker from './MenuItemPicker'

const money = n => `$${(Number(n) || 0).toFixed(2)}`
const ghost = { textTransform: 'none', fontWeight: 500, color: C.text, borderRadius: '8px' }
const field = { '& .MuiInputBase-root': { fontSize: 14, bgcolor: C.card } }
const HOUR = 60 * 60 * 1000

const EMPTY = {
  customerName: '',
  customerPhone: '',
  apartment: '',
  entryCode: '',
  amount: '',
  tax: '',
  tip: '',
  when: 'ASAP',
  readyAt: '',
  note: '',
}

function Section({ title, children }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>{title}</Box>
      {children}
    </Box>
  )
}

function Choice({ selected, onSelect, title, children }) {
  return (
    <Box
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onSelect() } }}
      sx={{
        display: 'flex', gap: 1, p: 1.25, borderRadius: '10px', cursor: 'pointer',
        border: `1px solid ${selected ? C.ink : C.border}`, bgcolor: selected ? C.subtle : C.card,
      }}
    >
      <Radio size="small" checked={selected} tabIndex={-1} sx={{ p: 0.25, alignSelf: 'flex-start' }} />
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>{title}</Box>
        <Box sx={{ fontSize: 12.5, color: C.muted, mt: 0.25, lineHeight: 1.45 }}>{children}</Box>
      </Box>
    </Box>
  )
}

// A datetime-local value ("2026-10-19T19:00") for a Date, in the browser's own clock.
const localInput = d => {
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * A restaurant's own delivery, typed in: an order the customer already paid it
 * for, by phone, at the counter or through another app. Its own drivers or
 * RushMyFood deliver it, and it shows on this board like any other order.
 */
export default function NewDeliveryDialog({ open, restaurantId, home, mapboxToken, onClose, onCreated }) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [form, setForm] = useState(EMPTY)
  const [address, setAddress] = useState(null) // { deliveryAddress, latitude, longitude }
  const [addressQuery, setAddressQuery] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [lines, setLines] = useState([])
  const [showMenu, setShowMenu] = useState(false)
  const [deliveredBy, setDeliveredBy] = useState('RUSHMYFOOD')
  const [error, setError] = useState('')
  const searchTimer = useRef(null)
  // One key per delivery: a retry after a dropped connection can't make two.
  const opId = useRef(null)

  const [loadQuote, { data: quoteData, loading: quoteLoading }] = useLazyQuery(MANUAL_DELIVERY_QUOTE, { fetchPolicy: 'network-only' })
  const [create, { loading: saving }] = useMutation(CREATE_MANUAL_DELIVERY)
  const quote = address ? quoteData?.manualDeliveryQuote : null

  useEffect(() => {
    if (!open) return
    setForm(EMPTY)
    setAddress(null)
    setAddressQuery('')
    setSuggestions([])
    setLines([])
    setShowMenu(false)
    setDeliveredBy(home?.selfDelivery ? 'SELF' : 'RUSHMYFOOD')
    setError('')
    opId.current = null
  }, [open, home])

  useEffect(() => () => clearTimeout(searchTimer.current), [])

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }))

  const onAddressType = value => {
    setAddressQuery(value)
    setAddress(null)
    clearTimeout(searchTimer.current)
    if (value.trim().length < 3) {
      setSuggestions([])
      return
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const near = home?.location ? `${home.location.lng},${home.location.lat}` : undefined
        setSuggestions(await searchPlaces(value, mapboxToken, { limit: 5, proximity: near, types: 'address,poi' }))
      } catch (e) {
        setSuggestions([])
      }
    }, 300)
  }

  const pickAddress = s => {
    setAddress({ deliveryAddress: s.placeName, latitude: s.lat, longitude: s.lng })
    setAddressQuery(s.placeName)
    setSuggestions([])
    loadQuote({ variables: { restaurantId, latitude: s.lat, longitude: s.lng } })
  }

  const addLine = line => setLines(ls => [...ls, { ...line, key: `${Date.now()}-${ls.length}` }])
  const stepLine = (key, delta) => setLines(ls => ls
    .map(l => (l.key === key ? { ...l, quantity: l.quantity + delta } : l))
    .filter(l => l.quantity > 0))

  const itemsTotal = lines.reduce((s, l) => s + l.unitPrice * l.quantity, 0)
  const foodTotal = lines.length ? itemsTotal : Number(form.amount) || 0
  const fee = quote ? money(quote.fee) : '—'
  const readyDate = form.when === 'LATER' && form.readyAt ? new Date(form.readyAt) : null
  const waits = readyDate && readyDate.getTime() - Date.now() > HOUR

  const ownDriversNote = () => {
    switch (quote?.fallbackToPlatform) {
      case 'NEVER': return 'If none of your drivers takes it, it waits for you.'
      case 'ALWAYS': return `RushMyFood riders are offered it first; you are charged ${fee} + tip if they deliver it.`
      case 'IF_STAFF_BUSY': return `If none of your drivers takes it, RushMyFood delivers it and you are charged ${fee} + tip.`
      default: return 'Pick the address to see what happens if none of your drivers takes it.'
    }
  }

  const submit = async () => {
    setError('')
    if (!form.customerName.trim()) return setError("Enter the customer's name.")
    if (form.customerPhone.replace(/\D/g, '').length < 10) return setError("Enter the customer's phone number.")
    if (!address) return setError('Pick the delivery address from the list.')
    if (!lines.length && !(Number(form.amount) > 0)) return setError('Enter the amount paid for the food, or add the items from your menu.')
    if (form.when === 'LATER' && !readyDate) return setError('Choose the ready time, or pick As soon as possible.')
    if (!opId.current) opId.current = `web-${restaurantId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

    const input = {
      restaurantId: String(restaurantId),
      customerName: form.customerName.trim(),
      customerPhone: form.customerPhone.trim(),
      address: {
        ...address,
        apartment: form.apartment.trim() || null,
        entryCode: form.entryCode.trim() || null,
      },
      items: lines.length ? buildOrderInput(lines) : null,
      orderAmount: lines.length ? null : Number(form.amount) || 0,
      taxationAmount: Number(form.tax) || 0,
      tip: Number(form.tip) || 0,
      readyAt: readyDate ? readyDate.toISOString() : null,
      note: form.note.trim() || null,
      deliveredBy,
      clientOpId: opId.current,
    }
    try {
      const { data } = await create({ variables: { input } })
      onCreated(data.createManualDelivery)
    } catch (e) {
      setError(e?.graphQLErrors?.[0]?.message || e.message || 'Could not create the delivery.')
    }
  }

  const money$ = { startAdornment: <InputAdornment position="start">$</InputAdornment> }

  return (
    // A dialog renders outside the board, so it takes the board's font itself.
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth fullScreen={fullScreen} PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '12px', fontFamily: FONT, '& button, & input, & textarea': { fontFamily: FONT } } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ fontWeight: 700, fontSize: 17, color: C.text }}>New delivery</Box>
        <Box sx={{ fontSize: 13, color: C.muted, fontWeight: 400 }}>For an order the customer already paid you for.</Box>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '12px !important' }}>
        <Section title="Customer">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
            <TextField size="small" label="Name" value={form.customerName} onChange={set('customerName')} inputProps={{ maxLength: 120 }} sx={field} autoFocus />
            <TextField size="small" label="Phone" type="tel" value={form.customerPhone} onChange={set('customerPhone')} inputProps={{ maxLength: 30 }} sx={field} />
          </Box>
        </Section>

        <Section title="Delivery address">
          <Box sx={{ position: 'relative' }}>
            <TextField
              size="small"
              fullWidth
              label="Search the address"
              value={addressQuery}
              onChange={e => onAddressType(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><PlaceOutlinedIcon sx={{ fontSize: 18, color: C.muted }} /></InputAdornment> }}
              helperText={addressQuery && !address ? 'Pick one from the list' : quote?.distanceMiles != null ? `${quote.distanceMiles} mi from the restaurant` : ' '}
              sx={field}
            />
            {suggestions.length > 0 && (
              <Paper elevation={4} sx={{ position: 'absolute', zIndex: 2, top: 42, left: 0, right: 0, borderRadius: '10px', overflow: 'hidden' }}>
                {suggestions.map(s => (
                  <Box
                    key={s.id}
                    component="button"
                    type="button"
                    onClick={() => pickAddress(s)}
                    sx={{ display: 'block', width: '100%', textAlign: 'left', px: 1.5, py: 1, border: 0, bgcolor: C.card, cursor: 'pointer', font: 'inherit', fontSize: 13.5, color: C.text, '&:hover': { bgcolor: C.mutedBg } }}
                  >
                    {s.placeName}
                  </Box>
                ))}
              </Paper>
            )}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
            <TextField size="small" label="Apt / suite" value={form.apartment} onChange={set('apartment')} inputProps={{ maxLength: 60 }} sx={field} />
            <TextField size="small" label="Entry code" value={form.entryCode} onChange={set('entryCode')} inputProps={{ maxLength: 60 }} sx={field} />
          </Box>
        </Section>

        <Section title="Food">
          {lines.length > 0 ? (
            <Box sx={{ border: `1px solid ${C.border}`, borderRadius: '10px' }}>
              {lines.map(l => (
                <Box key={l.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75, borderBottom: `1px solid ${C.borderSoft}` }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontSize: 14, color: C.text }}>{l.name}</Box>
                    {lineDetails(l) && <Box sx={{ fontSize: 12, color: C.muted }}>{lineDetails(l)}</Box>}
                  </Box>
                  <IconButton size="small" aria-label={l.quantity > 1 ? 'One fewer' : 'Remove'} onClick={() => stepLine(l.key, -1)}>
                    {l.quantity > 1 ? <RemoveIcon fontSize="small" /> : <DeleteOutlineIcon fontSize="small" />}
                  </IconButton>
                  <Box sx={{ minWidth: 16, textAlign: 'center', fontSize: 14, ...tnum }}>{l.quantity}</Box>
                  <IconButton size="small" aria-label="One more" onClick={() => stepLine(l.key, 1)}><AddIcon fontSize="small" /></IconButton>
                  <Box sx={{ width: 64, textAlign: 'right', fontSize: 14, color: C.text, ...tnum }}>{money(l.unitPrice * l.quantity)}</Box>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.25, py: 0.75, fontSize: 13, color: C.muted }}>
                <span>Items total (your menu prices)</span>
                <Box component="span" sx={{ color: C.text, fontWeight: 600, ...tnum }}>{money(itemsTotal)}</Box>
              </Box>
            </Box>
          ) : (
            <TextField
              size="small"
              label="Amount paid for the food"
              required
              type="number"
              value={form.amount}
              onChange={set('amount')}
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={money$}
              helperText="Or add the items from your menu below."
              sx={field}
            />
          )}
          <Box>
            <Button size="small" onClick={() => setShowMenu(s => !s)} sx={{ ...ghost, px: 1 }} startIcon={showMenu ? null : <AddIcon fontSize="small" />}>
              {showMenu ? 'Hide the menu' : 'Add items from the menu'}
            </Button>
          </Box>
          {showMenu && <MenuItemPicker restaurantId={restaurantId} onAdd={addLine} />}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
            <TextField size="small" label="Tax (optional)" type="number" value={form.tax} onChange={set('tax')} inputProps={{ min: 0, step: '0.01' }} InputProps={money$} sx={field} />
            <TextField size="small" label="Tip for the driver" type="number" value={form.tip} onChange={set('tip')} inputProps={{ min: 0, step: '0.01' }} InputProps={money$} sx={field} />
          </Box>
        </Section>

        <Section title="Ready time">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
            <Choice selected={form.when === 'ASAP'} onSelect={() => setForm(f => ({ ...f, when: 'ASAP' }))} title="As soon as possible">
              A driver is found now.
            </Choice>
            <Choice selected={form.when === 'LATER'} onSelect={() => setForm(f => ({ ...f, when: 'LATER', readyAt: f.readyAt || localInput(new Date(Date.now() + HOUR)) }))} title="At a set time">
              When the food will be ready.
            </Choice>
          </Box>
          {form.when === 'LATER' && (
            <TextField
              size="small"
              type="datetime-local"
              label="Ready at"
              value={form.readyAt}
              onChange={set('readyAt')}
              inputProps={{ min: localInput(new Date()), step: 900 }}
              InputLabelProps={{ shrink: true }}
              helperText={waits ? 'It will wait as Scheduled until it is due.' : ' '}
              sx={{ ...field, maxWidth: 260 }}
            />
          )}
        </Section>

        <Section title="Note for the driver">
          <TextField size="small" multiline minRows={2} placeholder="Gate code, which door, call on arrival…" value={form.note} onChange={set('note')} inputProps={{ maxLength: 500 }} sx={field} />
        </Section>

        <Section title="Who delivers">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} role="radiogroup" aria-label="Who delivers">
            <Choice selected={deliveredBy === 'SELF'} onSelect={() => setDeliveredBy('SELF')} title="My drivers">
              Your staff drivers. {ownDriversNote()}
            </Choice>
            <Choice selected={deliveredBy === 'RUSHMYFOOD'} onSelect={() => setDeliveredBy('RUSHMYFOOD')} title="RushMyFood Delivery">
              RushMyFood riders, then a partner courier. You are charged {fee} + tip.
              {quote?.summary && <Box component="span" sx={{ display: 'block', color: C.faint, mt: 0.25 }}>{quote.summary}.</Box>}
            </Choice>
          </Box>
          {quoteLoading && <Box sx={{ fontSize: 12, color: C.muted }}><CircularProgress size={11} sx={{ mr: 0.75, verticalAlign: '-1px' }} />Working out the fee…</Box>}
        </Section>

        {error && <Alert severity="error" sx={{ borderRadius: '8px', fontSize: 13 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Box sx={{ mr: 'auto', fontSize: 13, color: C.muted, ...tnum }}>
          Customer paid {money(foodTotal + (Number(form.tax) || 0) + (Number(form.tip) || 0))}
        </Box>
        <Button onClick={onClose} disabled={saving} sx={ghost}>Cancel</Button>
        <Button
          onClick={submit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
          sx={{ ...primaryButtonSx, px: 2 }}
        >
          {saving ? 'Creating…' : 'Create delivery'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
